import { BookOpen, CheckCircle } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useLocation } from "react-router-dom";
import { useTutorial } from "@/contexts/TutorialContext";
import { tutorials, TutorialDefinition } from "./tutorialDefinitions";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const categoryLabels: Record<TutorialDefinition['category'], string> = {
  player: 'Joueur',
  match: 'Matchs',
  credits: 'Crédits',
  tournaments: 'Tournois',
  association: 'Association',
};

export const TutorialDrawer = () => {
  const { showTutorial, hasCompleted, isDrawerOpen, closeDrawer } = useTutorial();

  const location = useLocation();
  const isAssociationContext = location.pathname.includes('/association');
  const filteredTutorials = tutorials.filter(t =>
    isAssociationContext ? t.category === 'association' : t.category !== 'association'
  );

  const handleTutorialClick = (tutorialId: string) => {
    closeDrawer();
    setTimeout(() => showTutorial(tutorialId), 150);
  };

  const completedCount = filteredTutorials.filter(t => hasCompleted(t.id)).length;

  return (
    <Sheet open={isDrawerOpen} onOpenChange={(open) => !open && closeDrawer()}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
        <SheetHeader className="mb-4 shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-accent" />
            Guides & Tutoriels
          </SheetTitle>
          <p className="text-sm text-muted-foreground">
            Apprenez à utiliser toutes les fonctionnalités de Pala Puntos
          </p>
        </SheetHeader>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {/* Progress indicator */}
          <div className="mb-6 p-3 rounded-lg bg-muted/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Progression</span>
              <span className="text-sm text-muted-foreground">
                {completedCount}/{filteredTutorials.length} terminés
              </span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all duration-500"
                style={{ width: `${(completedCount / filteredTutorials.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Tutorials list */}
          <div className="space-y-3 pb-6">
            {filteredTutorials.map((tutorial) => {
              const isCompleted = hasCompleted(tutorial.id);
              const IconComponent = tutorial.icon;

              return (
                <button
                  key={tutorial.id}
                  onClick={() => handleTutorialClick(tutorial.id)}
                  className={cn(
                    "w-full p-4 rounded-lg border transition-all duration-200 text-left",
                    "hover:border-accent hover:bg-accent/5",
                    isCompleted 
                      ? "border-border bg-muted/30" 
                      : "border-border bg-background"
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
                      isCompleted ? "bg-success/10" : "bg-accent/10"
                    )}>
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5 text-success" />
                      ) : (
                        <IconComponent className="w-5 h-5 text-accent" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className={cn(
                          "font-medium truncate",
                          isCompleted && "text-muted-foreground"
                        )}>
                          {tutorial.title}
                        </h3>
                        {!isCompleted && (
                          <Badge variant="secondary" className="bg-accent/10 text-accent-readable text-xs shrink-0">
                            Nouveau
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {tutorial.description}
                      </p>
                      <span className="text-xs text-muted-foreground mt-1 inline-block">
                        {categoryLabels[tutorial.category]} • {tutorial.slides.length} étapes
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
